/**
 * Client-side Web Push setup for the installed/home-screen app.
 *
 * - registerServiceWorker(): registers /sw.js (call once on app start).
 * - ensurePushSubscription(): requests notification permission (if not decided),
 *   subscribes to push, and sends the subscription to the server. Safe to call
 *   repeatedly — it no-ops if already subscribed, unsupported, or push is
 *   disabled server-side (no VAPID keys).
 *
 * iOS note: Web Push only works when the site is added to the Home Screen
 * (installed PWA) on iOS 16.4+, and the permission request must come from a
 * user gesture — so callers should also retry from a tap handler.
 */
import { apiGetVapidPublicKey, apiSubscribePush } from "./api";

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) output[i] = raw.charCodeAt(i);
  return output;
}

function pushSupported(): boolean {
  return (
    typeof navigator !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export async function registerServiceWorker(): Promise<void> {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
  try {
    await navigator.serviceWorker.register("/sw.js");
  } catch (err) {
    console.warn("[push] service worker registration failed:", err);
  }
}

let inFlight = false;

export async function ensurePushSubscription(): Promise<void> {
  if (!pushSupported() || inFlight) return;
  // Already granted-or-denied handling: only proceed if we can act.
  if (Notification.permission === "denied") return;

  inFlight = true;
  try {
    const { key, enabled } = await apiGetVapidPublicKey();
    if (!enabled || !key) return; // server has no VAPID keys configured

    if (Notification.permission === "default") {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") return;
    }

    const reg = await navigator.serviceWorker.ready;
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(key),
      });
    }
    await apiSubscribePush(sub.toJSON());
  } catch (err) {
    console.warn("[push] ensurePushSubscription failed:", err);
  } finally {
    inFlight = false;
  }
}
